import React, { useState, useEffect } from 'react';
import api, { listSellers, createSeller, updateSeller, deleteSeller, getSellerMetrics } from '../services/api';
import { Briefcase, Plus, Loader2, Users, DollarSign, TrendingUp, ShoppingCart, Award, Target, BarChart3, RefreshCw, Download, Filter, ChevronDown, ChevronUp, Eye, X, Edit3, Trash2, UserPlus, Percent, Trophy, Settings, Save, ToggleLeft, ToggleRight, CalendarClock } from 'lucide-react';
import AfterPaySection from '../components/AfterPaySection';
import SellerFormModal from '../components/SellerFormModal';
import SellerTimelineChart from '../components/SellerTimelineChart';

const Utms = () => {
  const [activeTab, setActiveTab] = useState('sellers');
  const [sellers, setSellers] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('last_30d');
  const [sectorFilter, setSectorFilter] = useState('');
  const [sectorList, setSectorList] = useState([]);
  const RANK_COLS_DEFAULT = [
    { k: 'totalSales', l: 'Vendas', visible: true },
    { k: 'revenue', l: 'Faturamento', visible: true },
    { k: 'commission', l: 'Comissão', visible: true },
    { k: 'spend', l: 'Investido', visible: true },
    { k: 'profit', l: 'Lucro', visible: true },
    { k: 'roas', l: 'ROAS', visible: true },
    { k: 'roi', l: 'ROI', visible: false },
    { k: 'cpl', l: 'CPL', visible: true },
    { k: 'avgTicket', l: 'Ticket Médio', visible: true },
    { k: 'scheduledValue', l: 'Agendamentos', visible: true },
    { k: 'conversionRate', l: 'Conversão', visible: false },
    { k: 'leads', l: 'Leads', visible: false },
    { k: 'upsellPercent', l: '% Upsell', visible: false },
    { k: 'goalPercent', l: 'Meta', visible: true },
  ];
  const [rankCols, setRankCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('jtracker_ranking_columns') || 'null');
      if (Array.isArray(saved)) {
        return RANK_COLS_DEFAULT.map(d => ({ ...d, visible: (saved.find(s => s.k === d.k) || d).visible }));
      }
    } catch (e) {}
    return RANK_COLS_DEFAULT;
  });
  const [showColPicker, setShowColPicker] = useState(false);
  const [compareUtms, setCompareUtms] = useState([]);
  useEffect(() => { localStorage.setItem('jtracker_ranking_columns', JSON.stringify(rankCols.map(c => ({ k: c.k, visible: c.visible })))); }, [rankCols]);
  const rankFmt = (k, m) => {
    const v = m[k];
    if (k === 'totalSales' || k === 'leads') return v || 0;
    if (k === 'roas') return (v || 0).toFixed(2) + 'x';
    if (k === 'roi') return (v || 0).toFixed(1) + '%';
    if (k === 'conversionRate' || k === 'upsellPercent') return (v || 0).toFixed(1) + '%';
    if (k === 'goalPercent') return (v === null || v === undefined) ? '—' : v + '%';
    if (k === 'scheduledValue') return f(v || 0);
    return f(v || 0);
  };
  const rankColor = (k, m) => {
    if (k === 'revenue') return 'text-green-400';
    if (k === 'commission') return 'text-yellow-400';
    if (k === 'spend') return 'text-blue-400';
    if (k === 'profit') return (m.profit >= 0 ? 'text-green-400' : 'text-red-400') + ' font-medium';
    if (k === 'roas') return 'text-cyan-400';
    if (k === 'cpl') return 'text-purple-400';
    if (k === 'scheduledValue') return 'text-cyan-400';
    if (k === 'goalPercent') return m.goalPercent >= 100 ? 'text-green-400' : m.goalPercent >= 60 ? 'text-yellow-400' : 'text-red-400';
    return 'text-gray-300';
  };
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showAddSeller, setShowAddSeller] = useState(false);
  const [editingSeller, setEditingSeller] = useState(null);
  const [sellerForm, setSellerForm] = useState({ name: '', trackingType: 'utm_medium', trackingValues: [], fixedSalary: 0, commissionType: 'commission', commissionPercent: 0, commissionRemarketing: 0, commissionAfterPay: 0, goalRoas: 0, goalRoi: 0, goals: [], upsellProducts: [] });
  const [newUpsellProduct, setNewUpsellProduct] = useState('');
  const [newTrackingValue, setNewTrackingValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [sortBy, setSortBy] = useState('revenue');
  const [sortOrder, setSortOrder] = useState('desc');

  // UTM stats (legado)
  const [utmData, setUtmData] = useState(null);
  const [groupBy, setGroupBy] = useState('utmMedium');
  const [utmSortBy, setUtmSortBy] = useState('totalValue');

  const tabs = [
    { id: 'sellers', label: 'Vendedores', icon: Users },
    { id: 'ranking', label: 'Ranking', icon: Trophy },
    { id: 'metrics', label: 'Desempenho', icon: BarChart3 },
    { id: 'utms', label: 'Comparação UTMs', icon: Target },
    { id: 'afterpay', label: 'After Pay', icon: CalendarClock },
  ];

  useEffect(() => { loadAll(); }, [period, customStart, customEnd]);
  useEffect(() => {
    api.get('/sellers/sectors').then(r => { if (r.data?.success) setSectorList(r.data.sectors || []); }).catch(() => {});
  }, [sellers.length]);

  const getPeriodParams = () => {
    const now = new Date();
    const today = new Date(now.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
    const fmt = d => d.toISOString().split('T')[0];
    switch (period) {
      case 'today': return { start: fmt(today), end: fmt(today) };
      case 'yesterday': { const y = new Date(today); y.setDate(y.getDate()-1); return { start: fmt(y), end: fmt(y) }; }
      case 'this_week': { const d = new Date(today); d.setDate(d.getDate()-d.getDay()); return { start: fmt(d), end: fmt(today) }; }
      case 'last_week': { const d = new Date(today); d.setDate(d.getDate()-d.getDay()-7); const e = new Date(d); e.setDate(e.getDate()+6); return { start: fmt(d), end: fmt(e) }; }
      case 'this_month': { const d = new Date(today.getFullYear(), today.getMonth(), 1); return { start: fmt(d), end: fmt(today) }; }
      case 'last_month': { const d = new Date(today.getFullYear(), today.getMonth()-1, 1); const e = new Date(today.getFullYear(), today.getMonth(), 0); return { start: fmt(d), end: fmt(e) }; }
      case 'custom': return { start: customStart, end: customEnd };
      default: { const d = new Date(today); d.setDate(d.getDate()-30); return { start: fmt(d), end: fmt(today) }; }
    }
  };

  const loadAll = async () => {
    setLoading(true);
    const pp = getPeriodParams();
    if (period === 'custom' && (!customStart || !customEnd)) { setLoading(false); return; }
    try {
      const [sellersRes, metricsRes] = await Promise.all([
        listSellers().catch(() => ({ data: { sellers: [] } })),
        (pp.start
          ? api.get('/sellers/metrics', { params: { days: 'custom', start: pp.start, end: pp.end } })
          : api.get('/sellers/metrics', { params: { days: period } })
        ).catch(() => ({ data: { metrics: [] } }))
      ]);
      setSellers(sellersRes.data.sellers || []);
      setMetrics(metricsRes.data.metrics || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadUtmData = async () => {
    try {
      const pp = getPeriodParams();
      const res = await api.get('/utm/stats', { params: { start: pp.start, end: pp.end, groupBy } });
      setUtmData(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { if (activeTab === 'utms') loadUtmData(); }, [activeTab, period, groupBy]);
  useEffect(() => { setCompareUtms([]); }, [groupBy]);

  const handleSaveSeller = async () => {
    setSaving(true);
    try {
      if (editingSeller) {
        await updateSeller(editingSeller.id, sellerForm);
      } else {
        await createSeller(sellerForm);
      }
      setShowAddSeller(false);
      setEditingSeller(null);
      resetForm();
      loadAll();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDeleteSeller = async (id) => {
    if (!window.confirm('Excluir este vendedor?')) return;
    try { await deleteSeller(id); loadAll(); } catch (e) {}
  };

  const openEditSeller = (s) => {
    setEditingSeller(s);
    setSellerForm({ name: s.name, trackingType: s.trackingType, trackingValues: s.trackingValues || [], fixedSalary: s.fixedSalary || 0, commissionType: s.commissionType || 'commission', commissionPercent: s.commissionPercent || 0, commissionRemarketing: s.commissionRemarketing || 0, goalRoas: s.goalRoas || 0, goalRoi: s.goalRoi || 0 });
    setShowAddSeller(true);
  };

  const resetForm = () => { setSellerForm({ name: '', trackingType: 'utm_medium', trackingValues: [], fixedSalary: 0, commissionType: 'commission', commissionPercent: 0, commissionRemarketing: 0, commissionAfterPay: 0, goalRoas: 0, goalRoi: 0, goals: [], upsellProducts: [] }); };

  const addTrackingValue = () => {
    if (!newTrackingValue.trim()) return;
    const newValues = newTrackingValue.split(',').map(v => v.trim()).filter(v => v.length > 0);
    setSellerForm(p => ({ ...p, trackingValues: [...p.trackingValues, ...newValues] }));
    setNewTrackingValue('');
  };

  const removeTrackingValue = (idx) => { setSellerForm(p => ({ ...p, trackingValues: p.trackingValues.filter((_, i) => i !== idx) })); };

  const sortedMetrics = [...metrics].sort((a, b) => sortOrder === 'asc' ? (a[sortBy] || 0) - (b[sortBy] || 0) : (b[sortBy] || 0) - (a[sortBy] || 0));
  const handleSort = (key) => { setSortBy(key); setSortOrder(sortBy === key && sortOrder === 'desc' ? 'asc' : 'desc'); };

  const f = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fn = (v) => (v || 0).toLocaleString('pt-BR');

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-blue-500" size={40} /></div>;

  const totalRevenue = metrics.reduce((s, m) => s + m.revenue, 0);
  const totalSales = metrics.reduce((s, m) => s + m.totalSales, 0);
  const totalProfit = metrics.reduce((s, m) => s + m.profit, 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Briefcase className="text-blue-500" />Gestão Comercial</h1>
          <p className="text-gray-400 text-sm mt-1">Vendedores, métricas e gestão de performance</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-1 flex-wrap">
            {[
              { value: 'today', label: 'Hoje' },
              { value: 'yesterday', label: 'Ontem' },
              { value: 'this_week', label: 'Esta Semana' },
              { value: 'last_week', label: 'Sem. Anterior' },
              { value: 'this_month', label: 'Este Mês' },
              { value: 'last_month', label: 'Mês Anterior' },
              { value: 'last_30d', label: '30d' },
              { value: 'custom', label: 'Personalizado' },
            ].map(opt => (
              <button key={opt.value} onClick={() => setPeriod(opt.value)} className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap ${period === opt.value ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}>{opt.label}</button>
            ))}
          </div>
          {period === 'custom' && (
            <div className="flex items-center gap-2">
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-white text-sm" />
              <span className="text-gray-400 text-sm">até</span>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-white text-sm" />
            </div>
          )}
          <button onClick={loadAll} className="p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white"><RefreshCw size={20} /></button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-gray-800 p-1 rounded-xl overflow-x-auto">
        {tabs.map(t => (<button key={t.id} onClick={() => setActiveTab(t.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap ${activeTab === t.id ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}><t.icon size={18} />{t.label}</button>))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700"><div className="flex items-center gap-3"><div className="p-3 bg-blue-600/20 rounded-lg"><Users className="text-blue-500" size={24} /></div><div><p className="text-gray-400 text-sm">Vendedores</p><p className="text-2xl font-bold text-white">{sellers.filter(s => s.isActive).length}</p></div></div></div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700"><div className="flex items-center gap-3"><div className="p-3 bg-green-600/20 rounded-lg"><ShoppingCart className="text-green-500" size={24} /></div><div><p className="text-gray-400 text-sm">Vendas</p><p className="text-2xl font-bold text-white">{fn(totalSales)}</p></div></div></div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700"><div className="flex items-center gap-3"><div className="p-3 bg-yellow-600/20 rounded-lg"><DollarSign className="text-yellow-500" size={24} /></div><div><p className="text-gray-400 text-sm">Faturamento</p><p className="text-2xl font-bold text-white">{f(totalRevenue)}</p></div></div></div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700"><div className="flex items-center gap-3"><div className="p-3 bg-purple-600/20 rounded-lg"><TrendingUp className="text-purple-500" size={24} /></div><div><p className="text-gray-400 text-sm">Lucro</p><p className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{f(totalProfit)}</p></div></div></div>
      </div>

      {/* ===== VENDEDORES TAB ===== */}
      {activeTab === 'sellers' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-bold text-white">Vendedores ({sellers.length})</h2>
            <div className="flex items-center gap-2">
              <select value={sectorFilter} onChange={(e) => setSectorFilter(e.target.value)} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
                <option value="">Todos os setores</option>
                {sectorList.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={() => { resetForm(); setEditingSeller(null); setShowAddSeller(true); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500"><UserPlus size={18} />Novo Vendedor</button>
            </div>
          </div>

          {sellers.length === 0 ? (
            <div className="bg-gray-800 rounded-xl p-12 text-center"><Users size={48} className="mx-auto text-gray-600 mb-4" /><p className="text-gray-400">Nenhum vendedor cadastrado</p></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sellers.filter(s => !sectorFilter || s.sector === sectorFilter).map(s => {
                const m = metrics.find(mt => mt.id === s.id) || {};
                return (
                  <div key={s.id} className={`bg-gray-800 rounded-xl p-5 border ${s.isActive ? 'border-gray-700' : 'border-gray-700/50 opacity-60'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-white font-bold text-lg">{s.name}</h3>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEditSeller(s)} className="p-1.5 text-gray-400 hover:text-white"><Edit3 size={16} /></button>
                        <button onClick={() => handleDeleteSeller(s.id)} className="p-1.5 text-gray-400 hover:text-red-400"><Trash2 size={16} /></button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {s.sector && <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded text-xs">{s.sector}</span>}
                      {(s.trackingValues || []).map((v, i) => (<span key={i} className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">{v}</span>))}
                      <span className="px-2 py-0.5 bg-gray-700 text-gray-400 rounded text-xs">{s.trackingType}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div><p className="text-gray-500">Vendas</p><p className="text-white font-semibold">{m.totalSales || 0}</p></div>
                      <div><p className="text-gray-500">Faturamento</p><p className="text-green-400 font-semibold">{f(m.revenue)}</p></div>
                      <div><p className="text-gray-500">ROAS</p><p className="text-blue-400 font-semibold">{(m.roas || 0).toFixed(2)}x</p></div>
                      <div><p className="text-gray-500">Salário</p><p className="text-yellow-400 font-semibold">{f(m.salary)}</p></div>
                      <div><p className="text-gray-500">Agendamentos</p><p className="text-cyan-400 font-semibold">{m.scheduledCount || 0} <span className="text-xs text-gray-500">({f(m.scheduledValue)})</span></p></div>
                      <div>
                        <p className="text-gray-500">Meta</p>
                        {m.goalPercent === null || m.goalPercent === undefined ? (
                          <p className="text-gray-600 font-semibold">—</p>
                        ) : (
                          <div>
                            <p className={`font-semibold ${m.goalPercent >= 100 ? 'text-green-400' : m.goalPercent >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>{m.goalPercent}%</p>
                            <div className="w-full bg-gray-700 rounded-full h-1.5 mt-1">
                              <div className={`h-1.5 rounded-full ${m.goalPercent >= 100 ? 'bg-green-400' : m.goalPercent >= 60 ? 'bg-yellow-400' : 'bg-red-400'}`} style={{ width: Math.min(m.goalPercent, 100) + '%' }}></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===== RANKING TAB ===== */}
      {activeTab === 'ranking' && (
        <div className="space-y-4">
          {/* Fileira fixa (revisao 15/07): inclui Agendamentos em R$ */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-800 rounded-xl p-4"><span className="text-gray-400 text-xs">Faturamento Total</span><div className="text-xl font-bold text-green-400 mt-1">{f(sortedMetrics.reduce((s, m) => s + (m.revenue || 0), 0))}</div></div>
            <div className="bg-gray-800 rounded-xl p-4"><span className="text-gray-400 text-xs">Comissão Total</span><div className="text-xl font-bold text-yellow-400 mt-1">{f(sortedMetrics.reduce((s, m) => s + (m.commission || 0), 0))}</div></div>
            <div className="bg-gray-800 rounded-xl p-4"><span className="text-gray-400 text-xs">Agendamentos</span><div className="text-xl font-bold text-cyan-400 mt-1">{f(sortedMetrics.reduce((s, m) => s + (m.scheduledValue || 0), 0))}</div></div>
            <div className="bg-gray-800 rounded-xl p-4"><span className="text-gray-400 text-xs">Lucro Total</span><div className="text-xl font-bold text-white mt-1">{f(sortedMetrics.reduce((s, m) => s + (m.profit || 0), 0))}</div></div>
          </div>
          <div className="flex justify-end relative">
            <button onClick={() => setShowColPicker(!showColPicker)} className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 text-sm hover:text-white">Colunas <ChevronDown size={14} /></button>
            {showColPicker && (
              <div className="absolute top-10 right-0 bg-gray-800 border border-gray-700 rounded-xl p-3 z-20 w-52 space-y-1 shadow-xl">
                {rankCols.map(c => (
                  <label key={c.k} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer hover:text-white py-0.5">
                    <input type="checkbox" checked={c.visible} onChange={() => setRankCols(cols => cols.map(x => x.k === c.k ? { ...x, visible: !x.visible } : x))} className="rounded" />
                    {c.l}
                  </label>
                ))}
              </div>
            )}
          </div>
          {sortedMetrics.length >= 3 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {sortedMetrics.slice(0, 3).map((m, idx) => (
                <div key={m.id} className={`bg-gradient-to-br ${idx === 0 ? 'from-yellow-600/20 to-yellow-900/20 border-yellow-600/50' : idx === 1 ? 'from-gray-400/20 to-gray-600/20 border-gray-500/50' : 'from-orange-600/20 to-orange-900/20 border-orange-600/50'} rounded-xl p-5 border`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2"><Trophy className={idx === 0 ? 'text-yellow-500' : idx === 1 ? 'text-gray-400' : 'text-orange-500'} size={24} /><span className="text-lg font-bold text-white">#{idx + 1}</span></div>
                    <span className="text-2xl font-bold text-white">{m.name}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div><p className="text-gray-400">Vendas</p><p className="text-white font-bold">{m.totalSales}</p></div>
                    <div><p className="text-gray-400">Faturamento</p><p className="text-green-400 font-bold">{f(m.revenue)}</p></div>
                    <div><p className="text-gray-400">ROAS</p><p className="text-blue-400 font-bold">{m.roas.toFixed(2)}x</p></div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-900/50 border-b border-gray-700">
                <tr>
                  <th className="p-3 text-left text-gray-400 text-sm">#</th>
                  <th className="p-3 text-left text-gray-400 text-sm">Vendedor</th>
                  {rankCols.filter(c => c.visible).map(c=>(
                    <th key={c.k} className="p-3 text-right text-gray-400 text-sm cursor-pointer hover:text-white" onClick={()=>handleSort(c.k)}>
                      <div className="flex items-center justify-end gap-1">{c.l}{sortBy===c.k && (sortOrder==='asc'?<ChevronUp size={14}/>:<ChevronDown size={14}/>)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {sortedMetrics.map((m, idx) => (
                  <tr key={m.id} className="hover:bg-gray-700/30">
                    <td className="p-3 text-gray-500 font-bold">{idx + 1}</td>
                    <td className="p-3 text-white font-medium">{m.name}</td>
                    {rankCols.filter(c => c.visible).map(c => (
                      <td key={c.k} className={`p-3 text-right ${rankColor(c.k, m)}`}>{rankFmt(c.k, m)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== METRICAS TAB ===== */}
      {activeTab === 'metrics' && (
        <div className="space-y-4">
          {sortedMetrics.map(m => (
            <div key={m.id} className="bg-gray-800 rounded-xl p-5 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold text-lg">{m.name}</h3>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium">{m.totalSales} vendas</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">Faturamento</p><p className="text-green-400 font-bold">{f(m.revenue)}</p></div>
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">Comissão</p><p className="text-yellow-400 font-bold">{f(m.commission)}</p></div>
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">Investido</p><p className="text-blue-400 font-bold">{f(m.spend)}</p></div>
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">Lucro</p><p className={`font-bold ${m.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{f(m.profit)}</p></div>
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">ROAS</p><p className="text-cyan-400 font-bold">{m.roas.toFixed(2)}x</p></div>
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">ROI</p><p className={`font-bold ${m.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>{m.roi.toFixed(1)}%</p></div>
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">CPL</p><p className="text-purple-400 font-bold">{f(m.cpl)}</p></div>
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">Ticket Médio</p><p className="text-gray-300 font-bold">{f(m.avgTicket)}</p></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mt-3">
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">Agendamentos</p><p className="text-cyan-400 font-bold">{m.scheduledCount || 0} <span className="text-xs text-gray-500">({f(m.scheduledValue)})</span></p></div>
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">Conv. Agendamentos</p><p className="text-cyan-400 font-bold">{(m.convScheduled || 0).toFixed(1)}%</p></div>
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">Conv. Real</p><p className="text-green-400 font-bold">{(m.convReal || 0).toFixed(1)}%</p></div>
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">Frustração</p><p className="text-red-400 font-bold">{(m.frustrationRate || 0).toFixed(1)}%</p></div>
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">Meta</p><p className={`font-bold ${m.goalPercent === null || m.goalPercent === undefined ? 'text-gray-600' : m.goalPercent >= 100 ? 'text-green-400' : m.goalPercent >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>{m.goalPercent === null || m.goalPercent === undefined ? '—' : m.goalPercent + '%'}</p></div>
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">Chargebacks</p><p className="text-red-400 font-bold">{m.chargebackCount}</p></div>
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">Reembolsos</p><p className="text-orange-400 font-bold">{f(m.refundValue)}</p></div>
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">Top Produto</p><p className="text-white font-bold text-xs truncate">{m.topProduct}</p></div>
                <div className="bg-gray-900/50 rounded-lg p-3"><p className="text-gray-500 text-xs">Salário Estimado</p><p className="text-yellow-400 font-bold">{f(m.salary)}</p></div>
              </div>
            </div>
          ))}
          {sortedMetrics.length === 0 && <div className="bg-gray-800 rounded-xl p-12 text-center text-gray-500">Cadastre vendedores para ver metricas</div>}
          <SellerTimelineChart />
        </div>
      )}

      {/* ===== UTMs TAB ===== */}
      {activeTab === 'afterpay' && (
        <AfterPaySection period={period === 'custom' ? 'custom' : period} start={customStart} end={customEnd} />
      )}

      {activeTab === 'utms' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 bg-gray-800 rounded-lg p-1 w-fit">
            {[{k:'utmMedium',l:'Medium'},{k:'utmSource',l:'Source'},{k:'utmCampaign',l:'Campaign'}].map(g => (
              <button key={g.k} onClick={() => setGroupBy(g.k)} className={`px-4 py-2 rounded-lg text-sm ${groupBy === g.k ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}>{g.l}</button>
            ))}
          </div>
          {/* Comparar UTMs (revisao 15/07): multipla selecao criando ranking so com elas */}
          <div className="bg-gray-800 rounded-xl p-4 space-y-2">
            <p className="text-gray-500 text-xs">Comparar UTMs (selecione 2 ou mais para filtrar o ranking e o gráfico)</p>
            <div className="flex flex-wrap gap-1">
              {(utmData?.stats || []).map((s) => {
                const key = s.name || '(vazio)';
                const on = compareUtms.includes(key);
                return (
                  <button key={key} onClick={() => setCompareUtms(prev => on ? prev.filter(x => x !== key) : [...prev, key])}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-900 border-gray-700 text-gray-400 hover:text-white'}`}>
                    {key}
                  </button>
                );
              })}
              {compareUtms.length > 0 && (
                <button onClick={() => setCompareUtms([])} className="px-2.5 py-1 rounded-full text-xs border border-red-500/50 text-red-400 hover:text-red-300">Limpar</button>
              )}
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-900/50 border-b border-gray-700">
                <tr>
                  <th className="p-3 text-left text-gray-400 text-sm">UTM</th>
                  <th className="p-3 text-right text-gray-400 text-sm">Vendas</th>
                  <th className="p-3 text-right text-gray-400 text-sm">Faturamento</th>
                  <th className="p-3 text-right text-gray-400 text-sm">Ticket Médio</th>
                  <th className="p-3 text-right text-gray-400 text-sm">Comissão</th>
                  <th className="p-3 text-right text-gray-400 text-sm">Agendamentos</th>
                  <th className="p-3 text-right text-gray-400 text-sm">Valor Agendado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {(utmData?.stats || []).filter(s => compareUtms.length === 0 || compareUtms.includes(s.name || '(vazio)')).map((s, i) => (
                  <tr key={i} className="hover:bg-gray-700/30">
                    <td className="p-3 text-white font-medium">{s.name || '(vazio)'}</td>
                    <td className="p-3 text-right text-gray-300">{s.count}</td>
                    <td className="p-3 text-right text-green-400">{f(s.totalValue)}</td>
                    <td className="p-3 text-right text-gray-300">{f(s.avgTicket)}</td>
                    <td className="p-3 text-right text-yellow-400">{f(s.totalCommission)}</td>
                    <td className="p-3 text-right text-cyan-400">{s.scheduledCount || 0}</td>
                    <td className="p-3 text-right text-cyan-400">{f(s.scheduledValue)}</td>
                  </tr>
                ))}
                {(!utmData?.stats || utmData.stats.length === 0) && <tr><td colSpan={7} className="p-8 text-center text-gray-500">Nenhum dado encontrado</td></tr>}
              </tbody>
            </table>
          </div>
          {/* Grafico de desempenho de UTMs (revisao 15/07) */}
          <SellerTimelineChart
            endpoint="/utm/timeline"
            title="Desempenho de UTMs ao longo do tempo"
            extraParams={`&groupBy=${groupBy}`}
            seriesLabel="UTMs (múltipla seleção)"
            selectedIds={compareUtms.length > 0 ? compareUtms : null}
          />
        </div>
      )}

      {/* Modal Adicionar/Editar Vendedor (revisao 15/07) */}
      {showAddSeller && (
        <SellerFormModal
          seller={typeof editingSeller === 'object' ? editingSeller : (sellers.find(x => x.id === editingSeller) || null)}
          onClose={() => { setShowAddSeller(false); setEditingSeller(null); }}
          onSaved={() => { setShowAddSeller(false); setEditingSeller(null); loadAll(); }}
        />
      )}
    </div>
  );
};

export default Utms;
